import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslate } from "@tolgee/react";
import toast from "react-hot-toast";
import { match } from "ts-pattern";
import { DateTime } from "luxon";
import {
  ActivityStatus,
  useClockInMutation,
  useVisitByIdQuery,
} from "../../api/generated/graphql";
import { useCareContext } from "../../providers";
import { useGeoLocation } from "../../hooks/geoLocation";
import { Detail } from "./Detail";
import { Title } from "./Title";
import { careStarted } from "../../typewriter/segment";
import { LateClockOutModal } from "../../components/core/lateClockOutModal/LateClockOutModal";
import { useVisitInfo } from "../../utils/visits";
import { attemptToStartSentryReplay } from "../../utils/sentryUtils";
import { Button, Variant } from "@frontend/lyng/button";
import { ShiftActivities } from "../../components/core/activities/ShiftActivities";
import { TabSection } from "@frontend/lyng/tabSection/TabSection";
import { CareRecipientList } from "./CareRecipientList";
import { NotesList } from "./NotesList";
import { Activity, Status } from "../../types";

const getButtonTypeFromStatus = (status: Status) =>
  match<Status, Variant>(status)
    .with("pending", () => "primary")
    .with("clockedIn", () => "primary")
    .with("clockedOut", () => "tertiary")
    .exhaustive();

type TabType = "activities" | "care-recipients" | "notes";

const isValidTab = (value: string | null): TabType | null =>
  match(value)
    .with("activities", (): TabType => "activities")
    .with("care-recipients", (): TabType => "care-recipients")
    .with("notes", (): TabType => "notes")
    .otherwise(() => null);

export const ShiftDetails = () => {
  const { id } = useParams();
  const { t } = useTranslate();
  const { viewer } = useCareContext().state;

  const [searchParams, setSearchParams] = useSearchParams();

  const { data, loading, error } = useVisitByIdQuery({
    variables: {
      visitInstanceId: id ?? "",
    },
  });

  const [clickLoading, setClickLoading] = useState(false);
  const [isLateClockOut, setIsLateClockOut] = useState(false);
  const [showLateClockOutModal, setShowLateClockOutModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<Status>("pending");
  const navigate = useNavigate();
  const [clockInMutation] = useClockInMutation();
  const { getPosition } = useGeoLocation();

  const filter = searchParams.get("tab");
  const currentTab: TabType = isValidTab(filter) ?? "activities";

  const handleTabClick = (tab: string) => {
    setSearchParams({ tab }, { replace: true });
  };

  const tabs = useMemo(
    () => [
      { id: "activities", label: t("activities") },
      { id: "care-recipients", label: t("careRecipients") },
      { id: "notes", label: t("notes") },
    ],
    [t],
  );

  const shift = useVisitInfo(data?.visitById);

  const readOnly = useMemo(() => {
    if (!data?.visitById || !viewer) {
      return false;
    }

    return !data.visitById.visitorsV2.some((v) => v.id === viewer?.id);
  }, [data, viewer]);

  useEffect(() => {
    if (shift) {
      setIsLateClockOut(shift.to < DateTime.now());
      setCurrentStatus(shift.status ?? "pending");
    }
  }, [shift]);

  const handleClick = useCallback(() => {
    Sentry.setTag("clockInButtonClickedShift", true);

    // Starting a replay when user clicks the clock in button
    attemptToStartSentryReplay();

    if (currentStatus === "clockedIn") {
      if (isLateClockOut && shift?.shouldShowLateClockOutModal) {
        Sentry.addBreadcrumb({
          category: "navigation",
          message: "User clocking out late",
          data: {
            visitId: shift?.id,
          },
          level: "warning",
        });

        setShowLateClockOutModal(true);
      } else {
        Sentry.addBreadcrumb({
          category: "navigation",
          message: "User clocking out",
          data: {
            visitId: shift?.id,
          },
          level: "info",
        });

        navigate({
          pathname: `./complete`,
        });
      }
      return;
    }

    Sentry.addBreadcrumb({
      category: "navigation",
      message: "User clocking in",
      data: {
        visitId: shift?.id,
      },
    });

    setClickLoading(true);
    getPosition()
      .then((position) => {
        const coords = position?.coords;
        const input = {
          visitInstanceId: id ?? "",
          clockInLocation:
            coords?.latitude && coords.longitude
              ? {
                  lat: coords.latitude,
                  lng: coords.longitude,
                }
              : undefined,
        };
        return clockInMutation({
          variables: {
            input,
          },
          refetchQueries: ["VisitById"],
        });
      })
      .then(() => {
        setCurrentStatus("clockedIn");
        careStarted({});
      })
      .catch((e) => {
        toast.error(e.message);
      })
      .finally(() => setClickLoading(false));
  }, [
    currentStatus,
    isLateClockOut,
    id,
    navigate,
    getPosition,
    clockInMutation,
    shift,
  ]);

  // Activities should be both the ones set to the home and the ones set to the care recipient through homestays
  const activities = useMemo(() => {
    return (shift?.activities ?? []).sort((a, b) => {
      return a.title.localeCompare(b.title);
    });
  }, [shift]);

  const dueActivities: Activity[] = useMemo(
    () =>
      activities.filter(
        (activity) =>
          activity.isDue || activity.status === ActivityStatus.Completed,
      ),
    [activities],
  );

  const notDueActivities = useMemo(
    () =>
      activities.filter(
        (activity) =>
          !activity.isDue && activity.status !== ActivityStatus.Completed,
      ),
    [activities],
  );

  const showButton = useMemo(() => {
    if (!data?.visitById) {
      return false;
    }

    return (
      !readOnly &&
      currentStatus !== "clockedOut" &&
      !data.visitById.cancelledAt &&
      shift?.from &&
      shift.from < DateTime.now().plus({ hours: 12 })
    );
  }, [readOnly, currentStatus, data?.visitById, shift]);

  if (loading || !viewer) {
    return <span>Loading shift data</span>;
  }

  if (error) {
    return <span>Error loading shift data</span>;
  }

  if (!data?.visitById) {
    return <span>No shift data</span>;
  }

  const getButtonText = (status: Status) =>
    match(status)
      .with("pending", () => t("shiftDetails.pendingButton"))
      .with("clockedIn", () => t("shiftDetails.clockedInButton"))
      .with("clockedOut", () => t("shiftDetails.clockedOutButton"))
      .exhaustive();

  const saveLateClockOutOption = () => {
    navigate({
      pathname: `./complete`,
    });
    return Promise.resolve();
  };

  const subject = data.visitById.subject;

  if (subject.__typename !== "CareHome") {
    return <span>Visit subject is not a care home</span>;
  }

  return (
    <div className="flex flex-col h-screen bg-main">
      <div className="absolute top-10 px-20">
        <Title visit={shift} />
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="pb-6">
          <Detail shift={shift} careHome={subject} />
        </div>

        <div className="sticky top-0 z-10 bg-main">
          <TabSection
            tabs={tabs}
            currentTab={currentTab}
            onChange={handleTabClick}
            theme="mobile"
          />
        </div>

        <div className="pb-48">
          {match(currentTab)
            .with("activities", () => (
              <div className="pt-4">
                <ShiftActivities
                  displayView={readOnly}
                  activities={dueActivities ?? []}
                  nonDueActivitiesCount={notDueActivities?.length ?? 0}
                  visitStatus={currentStatus}
                  visitInstanceId={shift?.id ?? ""}
                />
                {notDueActivities?.length !== 0 && (
                  <div className="flex mt-6 justify-center w-full px-6">
                    <Button
                      variant="secondary"
                      text={`${notDueActivities?.length ?? 0} ${t("activites.otherActivities")}`}
                      onClick={() => navigate("./other-activities")}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            ))
            .with("care-recipients", () => (
              <CareRecipientList
                careRecipients={shift?.careRecipients ?? []}
                shiftId={shift?.id ?? ""}
                activities={shift?.activities ?? []}
              />
            ))
            .with("notes", () => (
              <NotesList
                notes={data.visitById?.visitNotes ?? []}
                hasClockedIn={currentStatus !== "pending"}
              />
            ))
            .exhaustive()}
        </div>
      </div>

      {/* Fixed Button at Bottom */}
      {showButton && (
        <div className="fixed bottom-0 w-full bg-main z-30">
          <div className="px-6 pt-2 pb-8 bg-greyscale-800">
            <Button
              type="button"
              className="w-full"
              variant={getButtonTypeFromStatus(currentStatus)}
              text={getButtonText(currentStatus)}
              onClick={handleClick}
              loading={clickLoading}
            />
          </div>
        </div>
      )}
      <LateClockOutModal
        dismiss={() => setShowLateClockOutModal(false)}
        show={showLateClockOutModal}
        save={saveLateClockOutOption}
      />
    </div>
  );
};
